<script setup>
import { VaButton, VaCard, VaCardContent, VaCardTitle } from 'vuestic-ui'
import { computed, onMounted, ref } from 'vue'
import { doughnutConfig, lineOptions } from '../../components/va-charts/vaChartConfigs'
import { externalTooltipHandler } from '../../components/va-charts/external-tooltip'
import VaChart from '../../components/va-charts/VaChart.vue'
import { useAppStore } from '../../stores/appStore'
import { useReportStore } from '../../stores/report'

const serviceColors = ['#FF5879', '#FF9538', '#FFC64C', '#42B8B8', '#2F98E8', '#9B59B6', '#27AE60', '#E74C3C']

const optionsDoughnut = {
  ...doughnutConfig,
  plugins: {
    ...doughnutConfig.plugins,
    tooltip: {
      enabled: false,
      position: 'nearest',
      external: externalTooltipHandler,
    },
  },
}

const optionsDoughnut2 = {
  ...doughnutConfig,
  cutout: '50%',
  plugins: {
    ...doughnutConfig.plugins,
    tooltip: {
      enabled: false,
      position: 'nearest',
      external: externalTooltipHandler,
    },
  },
}

// Serviços (API)
const servicesChartData = computed(() => {
  const products = reportStore.data?.products ?? []
  if (products.length === 0) {
    return {
      labels: [],
      datasets: [{ backgroundColor: [], data: [] }],
    }
  }
  return {
    labels: products.map((p) => p.title),
    datasets: [
      {
        backgroundColor: products.map((_, i) => serviceColors[i % serviceColors.length]),
        data: products.map((p) => p.count),
      },
    ],
  }
})

// Engajamento/Cadastro (API)
const usersChartData = computed(() => {
  const total = reportStore.data?.total_customers ?? 0
  const complete = reportStore.data?.account_complete_count ?? 0
  const pending = total - complete
  return {
    labels: ['Cadastrado', 'Aguardando cadastro'],
    datasets: [
      {
        backgroundColor: ['rgb(245, 74, 0)', '#ECF0F1'],
        data: [complete, pending],
      },
    ],
  }
})

// Telemedicina - gráfico por dia (API)
const meetChartData = computed(() => {
  const chartPerDay = reportStore.data?.chart_per_day ?? {}
  const labels = Object.keys(chartPerDay).map(Number)
  const data = Object.values(chartPerDay)
  return {
    labels,
    datasets: [
      {
        label: 'Consultas',
        backgroundColor: 'rgb(245, 74, 0)',
        data,
      },
    ],
  }
})

// Consultas presenciais - mesmo gráfico por dia (API)
const realMeetChartData = computed(() => meetChartData.value)

// Período de uso - gráfico por dia da semana (API)
const weekdayChartData = computed(() => {
  const chartPerWeekday = reportStore.data?.chart_per_weekday ?? {}
  const entries = Object.values(chartPerWeekday)
  const sorted = [...entries].sort((a, b) => a.day_number - b.day_number)
  return {
    labels: sorted.map((e) => e.day_name_pt),
    datasets: [
      {
        label: 'Consultas',
        backgroundColor: 'rgb(245, 74, 0)',
        data: sorted.map((e) => e.count),
      },
    ],
  }
})

const reportStore = useReportStore()
const appStore = useAppStore()

const filters = ref({
  month: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
})

const monthNames = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

function formatFn(date) {
  const jsDate = new Date(date)
  return `${monthNames[jsDate.getMonth()]} - ${jsDate.getFullYear()}`
}

function formatDateValue(date) {
  if (!date) return ''
  return date.toISOString()
}

async function generateReport() {
  const date = filters.value.month ? new Date(filters.value.month) : new Date()
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  await reportStore.getDashboard(month, year)
}

onMounted(async () => {
  await generateReport()
})
</script>

<template>
  <div>
    <div class="flex items-center gap-4 flex-wrap mb-4">
      <h1 class="page-title !mb-0">Relatórios</h1>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div class="lg:col-span-4">
        <VaCard>
          <VaCardContent>
            <h5 class="font-bold text-xl mb-4">Filtros</h5>
            <form class="grid grid-cols-6 items-end gap-4" @submit.prevent="generateReport">
              <div>
                <VaDateInput
                  v-model="filters.month"
                  type="month"
                  :month-names="['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']"
                  class="w-full"
                  label="Mês"
                  :format="formatFn"
                  :format-value="formatDateValue"
                />
              </div>
              <div>
                <VaButton :disabled="appStore.loading" type="submit" color="primary" class="w-full">
                  <span v-if="appStore.loading">Gerando...</span>
                  <span v-else>Gerar Relatório</span>
                </VaButton>
              </div>
            </form>
          </VaCardContent>
        </VaCard>
      </div>

      <!-- <VaCard>
        <VaCardTitle class="pb-0!">
          <h1 class="card-title text-secondary font-bold uppercase">Saúde Física</h1>
        </VaCardTitle>
        <VaCardContent class="flex flex-row gap-1">
          <section class="w-1/2">
            <div class="text-2xl font-bold text-primary">75%</div>
            <p class="text-xs text-secondary">do seu time está fisicamente saudável</p>
            <div class="my-4 gap-2 flex flex-col">
              <div class="flex items-center">
                <span class="inline-block size-4 mr-2" :style="{ backgroundColor: 'rgb(245, 74, 0)' }"></span>
                <span class="text-secondary">Saudável</span>
              </div>
              <div class="flex items-center">
                <span class="inline-block size-4 mr-2" :style="{ backgroundColor: '#ECF0F1' }"></span>
                <span class="text-secondary">Precisa de atenção</span>
              </div>
            </div>
          </section>
          <div class="w-1/2 flex items-center h-full flex-1 lg:pl-16 pl-2 -mr-1">
            <VaChart
              :data="physicalChartData"
              class="chart chart--donut h-[90px] w-[90px]"
              type="doughnut"
              :options="optionsDoughnut"
            />
          </div>
        </VaCardContent>
      </VaCard>

      <VaCard>
        <VaCardTitle class="pb-0!">
          <h1 class="card-title text-secondary font-bold uppercase">Saúde Mental</h1>
        </VaCardTitle>
        <VaCardContent class="flex flex-row gap-1">
          <section class="w-1/2">
            <div class="text-2xl font-bold text-[#BA00F3]">75%</div>
            <p class="text-xs text-secondary">do seu time está mentalmente saudável</p>
            <div class="my-4 gap-2 flex flex-col">
              <div class="flex items-center">
                <span class="inline-block size-4 mr-2" :style="{ backgroundColor: '#BA00F3' }"></span>
                <span class="text-secondary">Saudável</span>
              </div>
              <div class="flex items-center">
                <span class="inline-block size-4 mr-2" :style="{ backgroundColor: '#ECF0F1' }"></span>
                <span class="text-secondary">Precisa de atenção</span>
              </div>
            </div>
          </section>
          <div class="w-1/2 flex items-center h-full flex-1 lg:pl-16 pl-2 -mr-1">
            <VaChart
              :data="menthalChartData"
              class="chart chart--donut h-[90px] w-[90px]"
              type="doughnut"
              :options="optionsDoughnut"
            />
          </div>
        </VaCardContent>
      </VaCard>

      <VaCard>
        <VaCardTitle class="pb-0!">
          <h1 class="card-title text-secondary font-bold uppercase">Risco de afastamento</h1>
        </VaCardTitle>
        <VaCardContent class="flex flex-row gap-1">
          <section class="w-1/2">
            <div class="text-2xl font-bold text-[#FF0000]">3</div>
            <p class="text-xs text-secondary">pessoas do seu time correm risco de afastamento</p>
            <div class="my-4 gap-2 flex flex-col">
              <div class="flex items-center">
                <span class="inline-block size-4 mr-2" :style="{ backgroundColor: '#FF0000' }"></span>
                <span class="text-secondary">Com risco</span>
              </div>
              <div class="flex items-center">
                <span class="inline-block size-4 mr-2" :style="{ backgroundColor: '#ECF0F1' }"></span>
                <span class="text-secondary">Sem risco</span>
              </div>
            </div>
          </section>
          <div class="w-1/2 flex items-center h-full flex-1 lg:pl-16 pl-2 -mr-1">
            <VaChart
              :data="riskChartData"
              class="chart chart--donut h-[90px] w-[90px]"
              type="doughnut"
              :options="optionsDoughnut"
            />
          </div>
        </VaCardContent>
      </VaCard>

      <VaCard>
        <VaCardTitle class="pb-0!">
          <h1 class="card-title text-secondary font-bold uppercase">Evolução</h1>
        </VaCardTitle>
        <VaCardContent class="flex flex-row gap-1">
          <section class="w-1/2">
            <div class="text-2xl font-bold text-[#1B9C16]">25%</div>
            <p class="text-xs text-secondary">de evolução com relação ao mês passado</p>
          </section>
          <div class="w-1/2 flex items-center h-full flex-1 lg:pl-16 pl-2 -mr-1">
            <VaChart
              :data="evolutionChartData"
              class="chart chart--donut h-[90px] w-[90px]"
              type="doughnut"
              :options="optionsDoughnut"
            />
          </div>
        </VaCardContent>
      </VaCard> -->

      <VaCard class="lg:col-span-2">
        <VaCardTitle class="pb-0!">
          <h1 class="card-title text-secondary font-bold uppercase">Telemedicina</h1>
        </VaCardTitle>
        <VaCardContent class="space-y-4">
          <section>
            <div class="text-2xl font-bold text-primary">{{ reportStore.data?.telemedicine_count ?? '-' }}</div>
            <p class="text-xs whitespace-nowrap text-secondary">consultas este mês</p>
          </section>
          <div>
            <VaChart
              :data="meetChartData"
              class="chart chart--donut h-[90px] w-[90px]"
              type="line"
              :options="lineOptions"
            />
          </div>
        </VaCardContent>
      </VaCard>

      <VaCard class="lg:col-span-2">
        <VaCardTitle class="pb-0!">
          <h1 class="card-title text-secondary font-bold uppercase">Consultas presenciais</h1>
        </VaCardTitle>
        <VaCardContent class="space-y-4">
          <section>
            <div class="text-2xl font-bold text-primary">{{ reportStore.data?.presencial_count ?? '-' }}</div>
            <p class="text-xs whitespace-nowrap text-secondary">consultas este mês</p>
          </section>
          <div>
            <VaChart
              :data="realMeetChartData"
              class="chart chart--donut h-[90px] w-[90px]"
              type="line"
              :options="lineOptions"
            />
          </div>
        </VaCardContent>
      </VaCard>

      <VaCard class="lg:col-span-2">
        <VaCardTitle class="pb-0!">
          <h1 class="card-title text-secondary font-bold uppercase">Período de uso</h1>
        </VaCardTitle>
        <VaCardContent class="space-y-4">
          <div class="flex flex-row gap-1">
            <section>
              <div class="text-2xl font-bold text-primary">
                {{ reportStore.data?.busiest_day_of_week?.day_name_pt ?? '-' }}
              </div>
              <p class="text-xs whitespace-nowrap text-secondary">é o dia da semana com mais consultas</p>
            </section>
          </div>
          <div>
            <VaChart
              :data="weekdayChartData"
              class="chart chart--donut h-[90px] w-[90px]"
              type="line"
              :options="lineOptions"
            />
          </div>
        </VaCardContent>
      </VaCard>

      <VaCard>
        <VaCardTitle class="pb-0!">
          <h1 class="card-title text-secondary font-bold uppercase">Uso dos serviços</h1>
        </VaCardTitle>
        <VaCardContent class="flex flex-row gap-1">
          <section class="w-1/2">
            <div class="text-2xl font-bold text-primary">{{ reportStore.data?.products?.length ?? '-' }}</div>
            <p class="text-xs whitespace-nowrap text-secondary">serviços foram utilizados neste mês</p>
            <div class="my-4 gap-2 flex flex-col max-h-48 overflow-auto">
              <div
                v-for="(product, i) in reportStore.data?.products ?? []"
                :key="product.title"
                class="flex items-center"
              >
                <span
                  class="inline-block size-4 mr-2"
                  :style="{ backgroundColor: serviceColors[i % serviceColors.length] }"
                ></span>
                <span class="text-secondary">{{ product.title }}</span>
              </div>
            </div>
          </section>
          <div class="w-1/2 flex items-center h-full flex-1 lg:pl-16 pl-2 -mr-1">
            <VaChart
              :data="servicesChartData"
              class="chart chart--donut h-[200px] w-[200px]"
              type="doughnut"
              :options="optionsDoughnut2"
            />
          </div>
        </VaCardContent>
      </VaCard>

      <VaCard>
        <VaCardTitle class="pb-0!">
          <h1 class="card-title text-secondary font-bold uppercase">Engajamento</h1>
        </VaCardTitle>
        <VaCardContent class="space-y-2">
          <div class="flex flex-row gap-1">
            <section class="w-1/2">
              <div class="text-2xl font-bold text-primary">
                {{ reportStore.data?.account_complete_percentage ?? '-' }}%
              </div>
              <p class="text-xs whitespace-nowrap text-secondary">
                da sua base de funcionários<br />
                está cadastrada
              </p>
              <div class="my-4 gap-2 flex flex-col">
                <div class="flex items-center">
                  <span class="inline-block size-4 mr-2 bg-primary"></span>
                  <span class="text-secondary">Cadastrado</span>
                </div>
                <div class="flex items-center">
                  <span class="inline-block size-4 mr-2" :style="{ backgroundColor: '#ECF0F1' }"></span>
                  <span class="text-secondary">Aguardando cadastro</span>
                </div>
              </div>
            </section>
            <div class="w-1/2 flex items-center h-full flex-1 lg:pl-16 pl-2 -mr-1">
              <VaChart
                :data="usersChartData"
                class="chart chart--donut h-[90px] w-[90px]"
                type="doughnut"
                :options="optionsDoughnut"
              />
            </div>
          </div>

          <p class="text-xs text-center text-secondary">
            Reenvie a mensagem de boas vindas para relembrar os usuários de finalizarem o seu cadastro.
          </p>

          <VaButton class="w-full" @click.prevent> Reenviar </VaButton>
        </VaCardContent>
      </VaCard>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
